import { useEffect } from 'react'
import { ThunderboltOutlined } from '@ant-design/icons'
import { ThemedLayout, RefineThemes, useNotificationProvider } from '@refinedev/antd'
import { Authenticated, Refine } from '@refinedev/core'
import routerProvider from '@refinedev/react-router'
import { App as AntdApp, ConfigProvider } from 'antd'
import type { Locale } from 'antd/es/locale'
import enUS from 'antd/locale/en_US'
import koKR from 'antd/locale/ko_KR'
import viVN from 'antd/locale/vi_VN'
import { useTranslation } from 'react-i18next'
import { BrowserRouter, Routes, Route, Outlet, Navigate } from 'react-router'

import { renderSimpleRoutes } from './components/AuthenticatedRoutes'
import { DealerHeader } from './components/DealerHeader'
import { DealerSider } from './components/DealerSider'
import { ErrorBoundary } from './components/ErrorBoundary'
import { FullPageSpinner } from './components/FullPageSpinner'
import { PopupHost } from './components/popup'
import { ProxySessionBanner } from './components/ProxySessionBanner'
import { resources } from './config/resources'
import { OAuthCallback } from './pages/auth'
import { Dashboard } from './pages/dashboard'
import { DealerAccess } from './pages/dealer-access'
import { Login } from './pages/login'
import { NotFound } from './pages/NotFound'
import { ProxyAccess } from './pages/proxy-access'
import { authProvider } from './providers/authProvider'
import { dataProvider } from './providers/dataProvider'
import { i18nProvider } from './shared/i18n/i18n-provider'

import './shared/i18n'
import '@refinedev/antd/dist/reset.css'
import './components/responsive/responsive.css'

const antdLocaleMap: Record<string, Locale> = {
  ko: koKR,
  en: enUS,
  vi: viVN,
}

function App() {
  const { t, i18n } = useTranslation()
  const notificationProvider = useNotificationProvider()
  const antdLocale = antdLocaleMap[i18n.language] ?? koKR

  useEffect(() => {
    document.title = t('layout.header.appTitle')
  }, [i18n.language, t])

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ConfigProvider theme={RefineThemes.Blue} locale={antdLocale}>
          {/*
            antd's `App.useApp()` reads a context that only <App> provides — without it the hook
            hands back empty objects, so the first `message.success(...)` in a mutation's
            onSuccess threw and everything after it (cache invalidation, modal close, redirect)
            never ran. The dealer saw the server accept the action while the screen stayed put.
            `component={false}` keeps the context without adding a wrapper element to the layout.
          */}
          <AntdApp component={false}>
            {/* 확인 질문(confirmPopup)이 이 App 문맥(테마·언어)으로 열리게 한다(개발항목 294). */}
            <PopupHost />
            <Refine
              routerProvider={routerProvider}
              dataProvider={dataProvider}
              authProvider={authProvider}
              notificationProvider={notificationProvider}
              i18nProvider={i18nProvider}
              resources={resources}
              options={{
                syncWithLocation: true,
                warnWhenUnsavedChanges: true,
                undoableTimeout: 5000,
                title: {
                  text: t('layout.header.appTitle'),
                  icon: <ThunderboltOutlined />,
                },
              }}
            >
              <Routes>
                {/*
                  슈퍼관리자 대행 접속(#273) — outside both auth wrappers: a dealer signed in on this
                  browser must not bounce the tab to their own dashboard before the code is traded.
                */}
                <Route path="/proxy-access" element={<ProxyAccess />} />
                <Route
                  element={
                    <Authenticated key="auth-pages" fallback={<Outlet />}>
                      <Navigate to="/" replace />
                    </Authenticated>
                  }
                >
                  <Route path="/login" element={<Login />} />
                  <Route path="/auth/kakao/callback" element={<OAuthCallback provider="KAKAO" />} />
                  <Route path="/auth/naver/callback" element={<OAuthCallback provider="NAVER" />} />
                  <Route path="/dealer-access" element={<DealerAccess />} />
                </Route>
                <Route
                  element={
                    <Authenticated
                      key="protected-routes"
                      redirectOnFail="/login"
                      loading={<FullPageSpinner />}
                    >
                      <ThemedLayout Header={DealerHeader} Sider={DealerSider}>
                        <ProxySessionBanner />
                        <Outlet />
                      </ThemedLayout>
                    </Authenticated>
                  }
                >
                  <Route index element={<Dashboard />} />
                  {renderSimpleRoutes()}
                  <Route path="*" element={<NotFound />} />
                </Route>
              </Routes>
            </Refine>
          </AntdApp>
        </ConfigProvider>
      </BrowserRouter>
    </ErrorBoundary>
  )
}

export default App
